/**
 * Altara Consumer Trust Infrastructure — Platform Admin Sidebar.
 *
 * IA (Feb 2026, post-COFI restructure):
 *   Overview                       → Executive Overview
 *   ┌ 1. Consumer Journeys         ← umbrella. Everything built to date.
 *   │    ├ 1.1 Consumer Intelligence
 *   │    ├ 1.2 Institution Workspace
 *   │    ├ 1.3 Trust Workflows        (heart of Altara)
 *   │    ├ 1.4 Consumer Education
 *   │    ├ 1.5 Governance
 *   │    └ 1.6 Consumer Trust Wallet  (new — the PWA)
 *   └ 2. Platform                  ← platform admin (flat)
 *
 *  Additional COFI outcome umbrellas (Advice, POG, Complaints, Vulnerable
 *  Customers, Regulatory Reporting, Digital & Cyber Conduct, etc.) will be
 *  added here once the user signs off on the menu list.
 */
import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { TID } from "@/constants/testIds";
import TenantSwitcher from "@/components/shell/TenantSwitcher";
import DivisionSwitcher from "@/components/shell/DivisionSwitcher";
import {
  LayoutDashboard, Users, Route, Radio, Activity, IdCard, Brain,
  Building2, Send, Lightbulb, BarChart3, Code2, Settings2,
  KeyRound, Fingerprint, ShieldCheck, ClipboardCheck, BookOpen, GraduationCap,
  FileCheck2, Workflow, ClipboardList, Sparkles, Award, Megaphone,
  LineChart, Bell, FolderKanban, UserCheck, ListTodo, ScrollText,
  Plug, Palette, ToggleLeft, Wallet, Database,
  ChevronDown, ChevronRight, Lock, Compass, BadgeCheck, FolderOpen,
  Home as HomeIcon, Layers, FileBarChart, Landmark,
} from "lucide-react";

/* ─── Data model ────────────────────────────────────────────
 * UMBRELLAS = top-level containers.
 * Each umbrella has EITHER:
 *   - `capabilities: [...]`  → nested groups (each with its own subs)
 *   - `subs: [...]`          → flat list of leaf nav items
 * Not both.
 * ------------------------------------------------------------ */

const CONSUMER_JOURNEYS_CAPABILITIES = [
  {
    key: "consumer-intelligence",
    name: "Consumer Intelligence",
    number: "1.1",
    landing: "/capability/consumer-intelligence",
    tid: "cap-consumer-intelligence",
    subs: [
      { to: "/consumers",              label: "Consumer Register",     icon: Users,    status: "live",   tid: "nav-consumers" },
      { to: "/journeys",               label: "Consumer Journeys",     icon: Route,    status: "live",   tid: "nav-journeys" },
      { to: "/wallet-intelligence",    label: "Trust Signals",         icon: Radio,    status: "live",   tid: "nav-trust-signals" },
      { to: "/timeline",               label: "Timeline",              icon: Activity, status: "live",   tid: "nav-timeline" },
      { to: "/consumer-trust-profile", label: "Consumer Trust Profile",icon: IdCard,   status: "future", tid: "nav-consumer-trust-profile" },
      { to: "/behaviour-insights",     label: "Behaviour Insights",    icon: Brain,    status: "future", tid: "nav-behaviour-insights" },
    ],
  },
  {
    key: "institution-workspace",
    name: "Institution Workspace",
    number: "1.2",
    landing: "/capability/institution-workspace",
    tid: "cap-institution-workspace",
    subs: [
      { to: "/dashboard",              label: "Institution Dashboard", icon: LayoutDashboard, status: "live",   tid: "nav-institution-dashboard" },
      { to: "/institutions",           label: "Institutions",          icon: Building2,       status: "live",   tid: "nav-institutions" },
      { to: "/requests",               label: "Trust Requests",        icon: Send,            status: "live",   tid: "nav-requests" },
      { to: "/recommendations",        label: "Recommendations",       icon: Lightbulb,       status: "live",   tid: "nav-recommendations" },
      { to: "/reports",                label: "Reports",               icon: BarChart3,       status: "live",   tid: "nav-reports" },
      { to: "/developer-portal",       label: "Developer Portal",      icon: Code2,           status: "future", tid: "nav-developer-portal" },
      { to: "/institution-settings",   label: "Settings",              icon: Settings2,       status: "future", tid: "nav-institution-settings" },
    ],
  },
  {
    key: "trust-workflows",
    name: "Trust Workflows",
    number: "1.3",
    landing: "/capability/trust-workflows",
    tid: "cap-trust-workflows",
    heart: true, // "The heart of Altara" — gold badge treatment
    subs: [
      { to: "/consent-ledger",                  label: "Consent",               icon: KeyRound,        status: "live",        tid: "nav-consent" },
      { to: "/workflows/identity",              label: "Identity Verification", icon: Fingerprint,     status: "live",        tid: "nav-identity" },
      { to: "/kyc-orchestration",               label: "KYC",                   icon: ShieldCheck,     status: "live",        tid: "nav-kyc" },
      { to: "/workflows/affordability",         label: "Affordability",         icon: ClipboardCheck,  status: "placeholder", tid: "nav-affordability" },
      { to: "/workflows/product-understanding", label: "Product Understanding", icon: BookOpen,        status: "placeholder", tid: "nav-product-understanding" },
      { to: "/workflows/learning-requests",     label: "Learning Requests",     icon: GraduationCap,   status: "placeholder", tid: "nav-learning-requests" },
      { to: "/trust-certificates",              label: "Trust Credentials",     icon: FileCheck2,      status: "live",        tid: "nav-trust-credentials" },
      { to: "/workflows/templates",             label: "Workflow Templates",    icon: Workflow,        status: "live",        tid: "nav-workflow-templates" },
    ],
  },
  {
    key: "consumer-education",
    name: "Consumer Education",
    number: "1.4",
    landing: "/capability/consumer-education",
    tid: "cap-consumer-education",
    subs: [
      { to: "/learning",               label: "Learning Hub",  icon: GraduationCap, status: "mock",        tid: "nav-learning" },
      { to: "/assessments",            label: "Assessments",   icon: ClipboardList, status: "mock",        tid: "nav-assessments" },
      { to: "/dev/corpus",             label: "NAVI",          icon: Sparkles,      status: "live",        tid: "nav-navi-corpus" },
      { to: "/education-certificates", label: "Certificates",  icon: Award,         status: "placeholder", tid: "nav-education-certs" },
      { to: "/campaigns",              label: "Campaigns",     icon: Megaphone,     status: "future",      tid: "nav-campaigns" },
      { to: "/education-analytics",    label: "Analytics",     icon: BarChart3,     status: "future",      tid: "nav-education-analytics" },
    ],
  },
  {
    key: "governance",
    name: "Governance",
    number: "1.5",
    landing: "/capability/governance",
    tid: "cap-governance",
    subs: [
      { to: "/evidence",               label: "Trust Evidence",        icon: ShieldCheck,   status: "live",        tid: "nav-trust-evidence" },
      { to: "/governance/outcomes",    label: "Outcome Governance",    icon: LineChart,     status: "live",        tid: "nav-outcome-governance" },
      { to: "/outcome-monitoring",     label: "Outcome Monitoring",    icon: LineChart,     status: "future",      tid: "nav-outcome-monitoring" },
      { to: "/cases",                  label: "Cases",                 icon: FolderKanban,  status: "future",      tid: "nav-cases" },
      { to: "/human-reviews",          label: "Human Reviews",         icon: UserCheck,     status: "future",      tid: "nav-human-reviews" },
      { to: "/tasks",                  label: "Tasks",                 icon: ListTodo,      status: "future",      tid: "nav-tasks" },
      { to: "/audit-trail",            label: "Audit Trail",           icon: ScrollText,    status: "placeholder", tid: "nav-audit-trail" },
      { to: "/alerts",                 label: "Alerts",                icon: Bell,          status: "future",      tid: "nav-alerts" },
      { to: "/reports",                label: "Reports",               icon: BarChart3,     status: "live",        tid: "nav-governance-reports" },
    ],
  },
  {
    key: "consumer-trust-wallet",
    name: "Consumer Trust Wallet",
    number: "1.6",
    landing: "/wallet",
    tid: "cap-consumer-trust-wallet",
    subs: [
      { to: "/wallet",                 label: "Wallet Home",           icon: HomeIcon,      status: "live", tid: "nav-wallet-home" },
      { to: "/wallet/passport",        label: "Trust Wallet",        icon: BadgeCheck,    status: "live", tid: "nav-wallet-passport" },
      { to: "/wallet/documents",       label: "My Financial Documents", icon: FolderOpen,    status: "live", tid: "nav-wallet-documents" },
      { to: "/wallet/institutions",    label: "Trusted Institutions",  icon: Building2,     status: "live", tid: "nav-wallet-institutions" },
      { to: "/wallet/investments",     label: "Investment Scenarios",  icon: LineChart,     status: "live", tid: "nav-wallet-investments" },
      { to: "/wallet/life-journeys",   label: "Life Journeys",         icon: Compass,       status: "live", tid: "nav-wallet-journeys" },
      { to: "/wallet/safety",          label: "Digital Safety",        icon: ShieldCheck,   status: "live", tid: "nav-wallet-safety" },
      { to: "/wallet/assistance",      label: "Assistance Requests",   icon: ClipboardList, status: "live", tid: "nav-wallet-assistance" },
      { to: "/demos",                  label: "Demo Simulations",      icon: Sparkles,      status: "live", tid: "nav-wallet-demos" },
    ],
  },
];

const REGULATORY_REPORTING_SUBS = [
  { to: "/regulatory/dashboard",              label: "Reporting Dashboard",     icon: FileBarChart, status: "live",        tid: "nav-reporting-dashboard" },
  { to: "/regulatory/dashboard",              label: "OMNI-Risk Return",         icon: Landmark,     status: "live", tid: "nav-omni-risk" },
  { to: "/regulatory/data-lake",              label: "Regulatory Data Lake",    icon: Database,     status: "future",      tid: "nav-reg-data-lake" },
  { to: "/regulatory/submissions",            label: "Submission History",       icon: FileCheck2,   status: "future",      tid: "nav-submissions" },
  { to: "/regulatory/fic-risk",               label: "FIC Risk Assessment",      icon: ShieldCheck,  status: "future",      tid: "nav-fic-risk" },
  { to: "/regulatory/pa-cbr",                 label: "Prudential (PA-CBR)",      icon: Building2,    status: "future",      tid: "nav-pa-cbr" },
  { to: "/regulatory/ombud",                  label: "Ombud Reporting",          icon: ScrollText,   status: "future",      tid: "nav-ombud-reporting" },
  { to: "/regulatory/readme",                 label: "Regulatory README",        icon: BookOpen,     status: "live",        tid: "nav-regulatory-readme" },
];

const UMBRELLAS = [
  {
    key: "consumer-journeys",
    name: "Consumer Journeys",
    number: "1",
    landing: "/capability/consumer-intelligence", // no dedicated umbrella page yet; land on first sub-cap
    tid: "umbrella-consumer-journeys",
    icon: Compass,
    capabilities: CONSUMER_JOURNEYS_CAPABILITIES,
  },
  {
    key: "regulatory-reporting",
    name: "Regulatory Reporting",
    number: "2",
    landing: "/regulatory/dashboard",
    tid: "umbrella-regulatory-reporting",
    icon: FileBarChart,
    subs: REGULATORY_REPORTING_SUBS,
  },
  {
    key: "platform",
    name: "Platform",
    number: "3",
    landing: "/capability/platform",
    tid: "umbrella-platform",
    icon: Layers,
    subs: [
      { to: "/institutions",           label: "Tenants",         icon: Building2,   status: "live",   tid: "nav-tenants" },
      { to: "/platform/tenants/entities", label: "Entities & Divisions", icon: Building2, status: "live", tid: "nav-tenant-entities" },
      { to: "/administration",         label: "Users",           icon: Users,       status: "live",   tid: "nav-users" },
      { to: "/administration",         label: "Roles",           icon: ShieldCheck, status: "live",   tid: "nav-roles" },
      { to: "/administration",         label: "API Keys",        icon: KeyRound,    status: "live",   tid: "nav-api-keys" },
      { to: "/platform/trust-utility", label: "Trust Utility",   icon: ShieldCheck, status: "live",   tid: "nav-trust-utility" },
      { to: "/platform/providers",     label: "Providers",       icon: Plug,        status: "live",   tid: "nav-providers" },
      { to: "/platform/branding",      label: "Branding",        icon: Palette,     status: "future", tid: "nav-branding" },
      { to: "/platform/feature-flags", label: "Feature Flags",   icon: ToggleLeft,  status: "future", tid: "nav-feature-flags" },
      { to: "/administration",         label: "Audit Logs",      icon: ScrollText,  status: "live",   tid: "nav-audit-logs" },
      { to: "/platform/health",        label: "Platform Health", icon: Activity,    status: "placeholder", tid: "nav-platform-health" },
      { to: "/platform/readme",        label: "Platform README", icon: BookOpen,    status: "live",   tid: "nav-platform-readme" },
    ],
  },
];

const ALTARA_LOGO = "https://customer-assets.emergentagent.com/job_413a4d1d-7808-4808-afdb-26c6e8aa4d99/artifacts/mgbxngl3_Altara%20New.png";

const STATUS_TAG = {
  live:        { label: "live",    bg: "#D1FAE5", color: "#065F46" },
  beta:        { label: "beta",    bg: "#DBEAFE", color: "#1E40AF" },
  mock:        { label: "mock",    bg: "#CCFBF1", color: "#0F766E" },
  placeholder: { label: "phase 1", bg: "#FEF3C7", color: "#92400E" },
  future:      { label: "future",  bg: "#F1F5F9", color: "#475569" },
};

/* ─── Auto-expand logic ─────────────────────────────────────
 * A capability is "open" when a matching route is active OR when the user
 * has manually toggled it. An umbrella auto-opens whenever any of its
 * nested capabilities/subs is on the active route.
 * ------------------------------------------------------------ */

function pathMatchesSubs(pathname, subs) {
  return subs.some((s) => pathname === s.to || pathname.startsWith(s.to + "/"));
}

function pathMatchesUmbrella(pathname, umbrella) {
  if (umbrella.subs) return pathMatchesSubs(pathname, umbrella.subs);
  return umbrella.capabilities.some(
    (c) => pathname === c.landing || pathMatchesSubs(pathname, c.subs),
  );
}

function useOpenState() {
  const { pathname } = useLocation();
  const [manual, setManual] = useState({});
  const isOpen = (key, autoOpen) => {
    if (key in manual) return manual[key];
    return !!autoOpen;
  };
  const toggle = (key, autoOpen) => {
    const current = isOpen(key, autoOpen);
    setManual((m) => ({ ...m, [key]: !current }));
  };
  return { isOpen, toggle, pathname };
}

export default function Sidebar() {
  const { isOpen, toggle, pathname } = useOpenState();

  return (
    <aside
      data-testid={TID.sidebar}
      className="w-72 shrink-0 border-r border-border bg-white/95 h-screen sticky top-0 flex flex-col"
    >
      {/* Brand ─────────────────────────────────────────── */}
      <div className="px-5 py-4 border-b border-border flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-sm altara-gradient-bg flex items-center justify-center text-white font-display font-bold">
          A
        </div>
        <div className="flex flex-col leading-none">
          <span className="font-display font-semibold text-[15px] text-altara-ink">Altara</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-altara-teal font-medium mt-0.5">
            Consumer Trust Infrastructure
          </span>
        </div>
      </div>

      {/* Primary nav ─────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-4" data-testid="admin-sidebar-nav">
        <TenantSwitcher />
        <div className="px-3 -mt-2 pb-1"><DivisionSwitcher /></div>

        <SectionLabel>Overview</SectionLabel>
        <NavItem to="/executive-overview" icon={Compass} label="Executive Overview" tid={TID.navDashboard} />

        <SectionLabel note="Everything Altara does — and what powers it.">
          Capabilities
        </SectionLabel>
        {UMBRELLAS.map((u) => {
          const autoOpen = pathMatchesUmbrella(pathname, u);
          return (
            <Umbrella
              key={u.key}
              umbrella={u}
              open={isOpen(u.key, autoOpen)}
              onToggle={() => toggle(u.key, autoOpen)}
              nested={{ isOpen, toggle, pathname }}
            />
          );
        })}
      </nav>

      {/* Data-lake indicator + brand footer */}
      <div className="px-4 py-3 border-t border-border space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-slate-500 rounded bg-slate-50 border border-slate-200 px-2 py-1.5">
          <Database className="w-3 h-3" style={{ color: "#0F766E" }} />
          <span>Data lake: <span className="font-semibold text-slate-700">MongoDB</span> · tenant-isolated</span>
        </div>
        <div className="flex items-center gap-2 pl-1">
          <img src={ALTARA_LOGO} alt="Altara" className="h-6 w-auto object-contain opacity-80" />
          <span className="text-[10px] text-muted-foreground">Part of Altara Ecosystem</span>
        </div>
      </div>
    </aside>
  );
}

/* ─── Building blocks ─────────────────────────────── */

function SectionLabel({ children, note }) {
  return (
    <div className="px-5 pt-5 pb-1">
      <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground">
        {children}
      </div>
      {note && (
        <div className="text-[10px] text-slate-400 mt-0.5 leading-tight max-w-[210px]">
          {note}
        </div>
      )}
    </div>
  );
}

/**
 * Umbrella — top-level collapsible container.
 * If umbrella.capabilities exists → renders nested CapabilityGroup rows.
 * Otherwise → renders umbrella.subs as a flat list (like Platform).
 */
function Umbrella({ umbrella, open, onToggle, nested }) {
  const location = useLocation();
  const isLandingActive = location.pathname === umbrella.landing;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="mb-1">
      <div className="flex items-stretch mx-3">
        <NavLink
          to={umbrella.landing}
          data-testid={umbrella.tid}
          className={cn(
            "flex-1 flex items-center gap-2 px-2 py-2 text-[13px] rounded-l-sm font-semibold",
            "text-slate-800 hover:bg-slate-50 transition",
            isLandingActive && "bg-altara-deep text-white hover:bg-altara-deep",
          )}
        >
          <span
            className={cn(
              "w-5 h-5 rounded-md text-[10px] font-display font-semibold flex items-center justify-center shrink-0",
              isLandingActive ? "bg-white/20 text-white" : "text-white",
            )}
            style={isLandingActive ? {} : { background: "#0A1F44" }}
          >
            {umbrella.number}
          </span>
          <span className="flex-1 truncate">{umbrella.name}</span>
        </NavLink>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${umbrella.tid}-toggle`}
          aria-label={open ? "Collapse" : "Expand"}
          className={cn(
            "px-1.5 rounded-r-sm border-l border-transparent",
            isLandingActive ? "bg-altara-deep text-white/70 hover:text-white" : "text-slate-400 hover:bg-slate-50 hover:text-slate-700",
          )}
        >
          <Chevron className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && (
        <div className="mt-1 ml-6 mr-3 border-l border-slate-100 pl-2 space-y-1">
          {umbrella.capabilities?.map((cap) => {
            const autoOpen = pathMatchesSubs(nested.pathname, cap.subs) || nested.pathname === cap.landing;
            return (
              <CapabilityGroup
                key={cap.key}
                cap={cap}
                open={nested.isOpen(cap.key, autoOpen)}
                onToggle={() => nested.toggle(cap.key, autoOpen)}
              />
            );
          })}
          {umbrella.subs && <SubList subs={umbrella.subs} parentKey={umbrella.key} />}
        </div>
      )}
    </div>
  );
}

/**
 * CapabilityGroup — second-level collapsible group inside an umbrella.
 * Header route + chevron toggle → expands into the sub-item list.
 */
function CapabilityGroup({ cap, open, onToggle }) {
  const location = useLocation();
  const isLandingActive = location.pathname === cap.landing;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div>
      <div className="flex items-stretch">
        <NavLink
          to={cap.landing}
          data-testid={cap.tid}
          className={cn(
            "flex-1 flex items-center gap-2 px-2 py-1.5 text-[12.5px] rounded-l-sm font-medium",
            "text-slate-700 hover:bg-slate-50 transition",
            isLandingActive && "bg-slate-100 text-altara-deep",
          )}
        >
          <span
            className="w-4 h-4 rounded text-[9px] font-mono font-semibold flex items-center justify-center shrink-0 text-white"
            style={{ background: cap.heart ? "#C9A24A" : "#334155" }}
          >
            {cap.number.split(".")[1]}
          </span>
          <span className="flex-1 truncate">{cap.name}</span>
        </NavLink>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${cap.tid}-toggle`}
          aria-label={open ? "Collapse" : "Expand"}
          className="px-1.5 rounded-r-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700"
        >
          <Chevron className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <div className="mt-0.5 ml-5 mr-1 border-l border-slate-100 pl-2 space-y-0.5">
          <SubList subs={cap.subs} parentKey={cap.key} />
        </div>
      )}
    </div>
  );
}

/** Flat list of leaf nav items — shared by both umbrella-flat and capability-nested cases. */
function SubList({ subs, parentKey }) {
  return subs.map((sub) => {
    const Icon = sub.icon;
    const tag = STATUS_TAG[sub.status];
    const disabled = sub.status === "future";
    if (disabled) {
      return (
        <div
          key={`${parentKey}-${sub.tid}-${sub.label}`}
          data-testid={sub.tid}
          className="flex items-center gap-2 px-2 py-1.5 text-[12px] rounded-sm text-slate-400 cursor-not-allowed"
        >
          <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
          <span className="flex-1 truncate">{sub.label}</span>
          <Lock className="w-2.5 h-2.5 text-slate-300 shrink-0" />
          {tag && <StatusTag tag={tag} />}
        </div>
      );
    }
    return (
      <NavLink
        key={`${parentKey}-${sub.tid}-${sub.label}`}
        to={sub.to}
        data-testid={sub.tid}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2 px-2 py-1.5 text-[12px] rounded-sm transition",
            "text-slate-600 hover:bg-slate-50 hover:text-altara-deep",
            isActive && "bg-slate-100 text-altara-deep font-semibold",
          )
        }
      >
        <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
        <span className="flex-1 truncate">{sub.label}</span>
        {tag && <StatusTag tag={tag} />}
      </NavLink>
    );
  });
}

function StatusTag({ tag }) {
  return (
    <span
      className="text-[8px] uppercase tracking-widest font-mono px-1 rounded shrink-0"
      style={{ background: tag.bg, color: tag.color }}
    >
      {tag.label}
    </span>
  );
}

function NavItem({ to, icon: Icon, label, tid }) {
  return (
    <NavLink
      to={to}
      data-testid={tid}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 mx-3 my-0.5 px-3 py-2 text-[13px] rounded-sm transition-colors",
          "text-slate-700 hover:bg-slate-50 hover:text-altara-deep",
          isActive && "bg-altara-deep text-white hover:bg-altara-deep hover:text-white",
        )
      }
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
      <span className="flex-1">{label}</span>
    </NavLink>
  );
}
